<template>
  <div class="period-dashboard">
    <section class="period-summary">
      <div class="summary-card summary-card-main">
        <span class="summary-label">Средняя активность команды</span>
        <strong class="summary-value">{{ teamSummary.averageActivity }}%</strong>
        <span class="summary-meta">
          {{ formatHours(teamSummary.averageActiveDuration) }} в среднем за день
        </span>
        <div class="summary-stats-grid">
          <div>
            <small>CRM</small>
            <strong>{{ formatHours(teamSummary.averageCrmDuration) }}</strong>
          </div>
          <div>
            <small>Звонки</small>
            <strong>{{ formatMinutes(teamSummary.averageCallsDuration) }}</strong>
          </div>
          <div>
            <small>Кол-во звонков</small>
            <strong>{{ teamSummary.totalCallCount }}</strong>
          </div>
          <div>
            <small>Сообщения</small>
            <strong>{{ teamSummary.totalMessageCount }}</strong>
          </div>
        </div>
      </div>

      <button
        type="button"
        class="summary-card"
        :class="{ active: statusFilter === 'normal' }"
        @click="$emit('summary-filter', statusFilter === 'normal' ? null : 'normal')"
      >
        <span class="summary-label">В норме</span>
        <strong class="summary-value status-normal">{{ teamSummary.normalCount }}</strong>
        <span class="summary-meta">стабильных сотрудников</span>
      </button>

      <button
        type="button"
        class="summary-card"
        :class="{ active: statusFilter === 'warning' }"
        @click="$emit('summary-filter', statusFilter === 'warning' ? null : 'warning')"
      >
        <span class="summary-label">Проседают</span>
        <strong class="summary-value status-warning">{{ teamSummary.warningCount }}</strong>
        <span class="summary-meta">требуют внимания</span>
      </button>

      <button
        type="button"
        class="summary-card"
        :class="{ active: statusFilter === 'critical' }"
        @click="$emit('summary-filter', statusFilter === 'critical' ? null : 'critical')"
      >
        <span class="summary-label">Критично</span>
        <strong class="summary-value status-critical">{{ teamSummary.criticalCount }}</strong>
        <span class="summary-meta">
          {{ teamSummary.riskDaysCount }} дней риска по команде
        </span>
      </button>
    </section>

    <section class="period-layout">
      <div class="period-main">
        <div class="period-block">
          <div class="period-block-head">
            <div>
              <div class="period-block-kicker">Heatmap</div>
              <h2>Ритм команды по дням</h2>
            </div>
            <button
              v-if="hasAnyFocus"
              type="button"
              class="period-action-link"
              @click="$emit('clear-focus')"
            >
              Сбросить фильтры
            </button>
          </div>

          <div
            ref="heatmap"
            class="heatmap-wrap"
            :class="{ 'heatmap-wrap-compact': isLongPeriod }"
          >
            <div class="heatmap-grid" :style="heatmapStyle">
              <div class="heatmap-corner"></div>
              <button
                v-for="day in days"
                :key="day"
                type="button"
                class="heatmap-day"
                :class="{ focused: focusedDay === day }"
                @click="$emit('focus-day', focusedDay === day ? null : day)"
              >
                <span>{{ formatDay(day) }}</span>
                <small>{{ formatWeekday(day) }}</small>
              </button>

              <template v-for="row in rows" :key="row.id">
                <button
                  type="button"
                  class="heatmap-user"
                  :class="{ active: selectedEmployeeId === row.id }"
                  @click="$emit('focus-employee', row.id)"
                >
                  <span>{{ row.name }}</span>
                  <small>{{ row.departmentName }}</small>
                </button>

                <button
                  v-for="cell in row.days"
                  :key="row.id + '-' + cell.day"
                  type="button"
                  class="heatmap-cell"
                  :class="[
                    'heatmap-cell-' + cell.color,
                    {
                      focused: focusedDay === cell.day,
                      dimmed: focusedDay && focusedDay !== cell.day,
                    },
                  ]"
                  :title="getCellTitle(row, cell)"
                  @click="$emit('open-day', { userId: row.id, day: cell.day })"
                >
                  <span>{{ cell.isNoData ? '—' : cell.activityPercent }}</span>
                </button>
              </template>
            </div>
          </div>
        </div>

        <div class="period-block">
          <div class="period-block-head">
            <div>
              <div class="period-block-kicker">Рейтинг</div>
              <h2>Топ сотрудников по активности</h2>
            </div>
            <div class="period-head-actions">
              <div class="period-block-meta">
                Звонки считаются от {{ callThresholdLabel }}
              </div>
              <button
                v-if="selectedEmployeeId"
                type="button"
                class="period-action-link"
                @click="$emit('clear-focus')"
              >
                Показать всех
              </button>
            </div>
          </div>

          <div class="ranking-table-wrap">
            <div class="ranking-table">
              <div class="ranking-head">
                <span>Сотрудник</span>
                <span>Средняя</span>
                <span>CRM</span>
                <span>Звонки</span>
                <span>Кол-во</span>
                <span>Сообщения</span>
                <span>Простой</span>
                <span>Риск</span>
                <span>Тренд</span>
              </div>

              <button
                v-for="user in ranking"
                :key="user.id"
                type="button"
                class="ranking-row"
                :class="{ active: selectedEmployeeId === user.id }"
                @click="$emit('focus-employee', user.id)"
              >
                <span class="ranking-name">
                  <strong>{{ user.name }}</strong>
                  <small>{{ user.departmentName }}</small>
                </span>
                <span>{{ user.averageActivity }}%</span>
                <span>{{ formatHours(user.averageCrmDuration) }}</span>
                <span>{{ formatMinutes(user.averageCallsDuration) }}</span>
                <span>{{ user.totalCallCount }}</span>
                <span>{{ user.totalMessageCount }}</span>
                <span>{{ formatHours(user.averageIdleDuration) }}</span>
                <span>{{ user.riskDaysCount }}</span>
                <span
                  :class="user.trend >= 0 ? 'trend-positive' : 'trend-negative'"
                  :title="getTrendTitle(user)"
                >
                  {{ user.trend >= 0 ? "+" : "" }}{{ user.trend }}%
                </span>
              </button>
            </div>
          </div>
        </div>

        <div class="period-block">
          <div class="period-block-head">
            <div>
              <div class="period-block-kicker">Активности</div>
              <h2>Срез по сотрудникам</h2>
            </div>
            <div class="period-head-actions">
              <div class="period-block-meta">
                В CRM, звонки, простой, сообщения и звонки от {{ callThresholdLabel }}
              </div>
              <button
                v-if="selectedEmployeeId"
                type="button"
                class="period-action-link"
                @click="$emit('clear-focus')"
              >
                Показать всех
              </button>
            </div>
          </div>

          <div class="ranking-table-wrap">
            <div class="ranking-table ranking-table-activity">
              <div class="ranking-head ranking-head-activity">
                <span>Сотрудник</span>
                <span>Активность</span>
                <span>В CRM</span>
                <span>Звонил</span>
                <span>Простой</span>
                <span>Звонков</span>
                <span>Сообщений</span>
              </div>

              <button
                v-for="user in ranking"
                :key="'activity-' + user.id"
                type="button"
                class="ranking-row ranking-row-activity"
                :class="{ active: selectedEmployeeId === user.id }"
                @click="$emit('focus-employee', user.id)"
              >
                <span class="ranking-name">
                  <strong>{{ user.name }}</strong>
                  <small>{{ user.departmentName }}</small>
                </span>
                <span>{{ user.averageActivity }}%</span>
                <span>{{ formatHours(user.averageCrmDuration) }}</span>
                <span>{{ formatMinutes(user.averageCallsDuration) }}</span>
                <span>{{ formatHours(user.averageIdleDuration) }}</span>
                <span>{{ user.totalCallCount }}</span>
                <span>{{ user.totalMessageCount }}</span>
              </button>
            </div>
          </div>
        </div>

        <div class="period-block">
          <div class="period-block-head">
            <div>
              <div class="period-block-kicker">Динамика</div>
              <h2>Средняя активность команды</h2>
            </div>
          </div>

          <div class="trend-chart">
            <svg viewBox="0 0 1000 240" preserveAspectRatio="none">
              <polyline
                class="trend-line"
                :points="trendPolyline"
              ></polyline>
              <line
                v-for="(point, index) in teamTrend"
                :key="point.day + '-grid'"
                class="trend-grid-line"
                :x1="getTrendX(index)"
                :x2="getTrendX(index)"
                y1="16"
                y2="216"
              ></line>
              <g
                v-for="(point, index) in teamTrend"
                :key="point.day"
                class="trend-point-group"
                @click="$emit('focus-day', point.day)"
              >
                <circle
                  class="trend-point"
                  :class="{ active: focusedDay === point.day }"
                  :cx="getTrendX(index)"
                  :cy="getTrendY(point.activityPercent)"
                  r="6"
                ></circle>
                <text
                  class="trend-point-label"
                  :x="getTrendX(index)"
                  y="232"
                  text-anchor="middle"
                >
                  {{ formatDay(point.day) }}
                </text>
              </g>
            </svg>
          </div>
        </div>

        <div class="period-block">
          <div class="period-block-head">
            <div>
              <div class="period-block-kicker">Инсайты</div>
              <h2>Что стоит разобрать с командой</h2>
            </div>
          </div>

          <div class="insights-list">
            <button
              v-for="insight in insights"
              :key="insight.id"
              type="button"
              class="insight-item"
              @click="$emit('apply-insight', insight)"
            >
              {{ insight.title }}
            </button>
            <div v-if="!insights.length" class="insight-empty">
              На выбранном периоде система не нашла явных проблемных паттернов.
            </div>
          </div>
        </div>

        <div v-if="employeeCard" class="period-block">
          <div class="period-block-head">
            <div>
              <div class="period-block-kicker">Карточка сотрудника</div>
              <h2>{{ employeeCard.name }}</h2>
            </div>
            <button
              type="button"
              class="period-action-link"
              @click="$emit('clear-focus')"
            >
              Показать всех
            </button>
          </div>

          <div class="employee-card">
            <div class="employee-metrics">
              <div class="employee-metric">
                <span>Средняя активность</span>
                <strong>{{ employeeCard.averageActivity }}%</strong>
              </div>
              <div class="employee-metric">
                <span>Работа в CRM</span>
                <strong>{{ formatHours(employeeCard.averageCrmDuration) }}</strong>
              </div>
              <div class="employee-metric">
                <span>Звонки</span>
                <strong>{{ formatMinutes(employeeCard.averageCallsDuration) }}</strong>
              </div>
              <div class="employee-metric">
                <span>Количество звонков</span>
                <strong>{{ employeeCard.totalCallCount }}</strong>
              </div>
              <div class="employee-metric">
                <span>Сообщения</span>
                <strong>{{ employeeCard.totalMessageCount }}</strong>
              </div>
              <div class="employee-metric">
                <span>Активное время</span>
                <strong>{{ formatHours(employeeCard.averageActiveDuration) }}</strong>
              </div>
              <div class="employee-metric">
                <span>Дни риска</span>
                <strong>{{ employeeCard.riskDaysCount }}</strong>
              </div>
              <div class="employee-metric">
                <span>Средний простой</span>
                <strong>{{ formatHours(employeeCard.averageIdleDuration) }}</strong>
              </div>
            </div>

            <div class="employee-sparkline">
              <div
                v-for="day in employeeCard.days"
                :key="day.day"
                class="employee-spark-bar"
                :class="'heatmap-cell-' + day.color"
                :style="{ height: Math.max(day.activityPercent, 8) + '%' }"
                :title="formatTooltipDay(day)"
                @click="$emit('open-day', { userId: employeeCard.id, day: day.day })"
              ></div>
            </div>

            <div class="employee-breakdown">
              <div class="employee-breakdown-card">
                <h3>Самые плохие дни</h3>
                <button
                  v-for="day in employeeCard.worstDays"
                  :key="day.day"
                  type="button"
                  class="employee-day-link"
                  @click="$emit('open-day', { userId: employeeCard.id, day: day.day })"
                >
                  <span>{{ formatLongDay(day.day) }}</span>
                  <strong>{{ day.activityPercent }}%</strong>
                  <small>{{ day.reason }}</small>
                </button>
              </div>

              <div class="employee-breakdown-card">
                <h3>Распределение по времени дня</h3>
                <div class="distribution-row">
                  <span>Утро</span>
                  <strong>{{ employeeCard.distributionPercent.morning }}%</strong>
                </div>
                <div class="distribution-row">
                  <span>День</span>
                  <strong>{{ employeeCard.distributionPercent.day }}%</strong>
                </div>
                <div class="distribution-row">
                  <span>Вечер</span>
                  <strong>{{ employeeCard.distributionPercent.evening }}%</strong>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import dayjs from "dayjs";

export default {
  emits: [
    "summary-filter",
    "focus-employee",
    "focus-day",
    "open-day",
    "apply-insight",
    "clear-focus",
  ],
  props: {
    teamSummary: { type: Object, required: true },
    ranking: { type: Array, required: true },
    rows: { type: Array, required: true },
    days: { type: Array, required: true },
    teamTrend: { type: Array, required: true },
    insights: { type: Array, required: true },
    selectedEmployeeId: { type: Number, default: null },
    employeeCard: { type: Object, default: null },
    focusedDay: { type: String, default: "" },
    statusFilter: { type: String, default: null },
    callThresholdSec: { type: Number, default: 0 },
  },
  computed: {
    hasAnyFocus() {
      return Boolean(this.selectedEmployeeId || this.focusedDay || this.statusFilter);
    },
    dayCellWidth() {
      if (this.days.length > 24) {
        return 52;
      }

      if (this.days.length > 14) {
        return 58;
      }

      return 64;
    },
    isLongPeriod() {
      return this.days.length > 14;
    },
    heatmapStyle() {
      const dayColumnWidth = this.isLongPeriod
        ? `${this.dayCellWidth}px`
        : `minmax(${this.dayCellWidth}px, 1fr)`;

      return {
        gridTemplateColumns: `220px repeat(${this.days.length}, ${dayColumnWidth})`,
      };
    },
    trendPolyline() {
      if (!this.teamTrend.length) {
        return "";
      }

      return this.teamTrend
        .map((point, index) => `${this.getTrendX(index)},${this.getTrendY(point.activityPercent)}`)
        .join(" ");
    },
    callThresholdLabel() {
      if (!this.callThresholdSec) {
        return "0 сек";
      }

      if (this.callThresholdSec < 60) {
        return `${this.callThresholdSec} сек`;
      }

      return `${Math.round(this.callThresholdSec / 60)} мин`;
    },
  },
  methods: {
    formatDay(day) {
      return dayjs(day).format("DD");
    },
    formatWeekday(day) {
      return new Intl.DateTimeFormat("ru-RU", {
        weekday: "short",
      }).format(new Date(dayjs(day).valueOf()));
    },
    formatLongDay(day) {
      return new Intl.DateTimeFormat("ru-RU", {
        day: "numeric",
        month: "long",
      }).format(new Date(dayjs(day).valueOf()));
    },
    formatHours(seconds) {
      const hours = seconds / 3600;
      return `${hours.toFixed(hours >= 10 ? 0 : 1)}ч`;
    },
    formatMinutes(seconds) {
      if (!seconds) {
        return "0 мин";
      }

      const minutes = Math.round(seconds / 60);
      return `${minutes} мин`;
    },
    formatTooltipDay(day) {
      return `${this.formatLongDay(day.day)} — ${day.activityPercent}%`;
    },
    getTrendTitle(user) {
      return `Тренд — изменение средней активности к предыдущему периоду такой же длины. Было ${user.previousAverage}%, стало ${user.averageActivity}%.`;
    },
    getCellTitle(user, cell) {
      return [
        `${user.name} — ${this.formatLongDay(cell.day)}`,
        `Активность: ${cell.activityPercent}%`,
        `Активно: ${this.formatHours(cell.activeDuration)}`,
        `Простой: ${this.formatHours(cell.idleDuration)}`,
        `Действия: ${cell.hasActions ? "были" : "не было"}`,
      ].join("\n");
    },
    getTrendX(index) {
      if (this.teamTrend.length <= 1) {
        return 40;
      }

      return 40 + (index / (this.teamTrend.length - 1)) * 920;
    },
    getTrendY(percent) {
      return 200 - (Math.max(percent, 0) / 100) * 160;
    },
    scrollToFocusedDay() {
      if (!this.focusedDay || !this.$refs.heatmap) {
        return;
      }

      const columnIndex = this.days.indexOf(this.focusedDay);
      if (columnIndex === -1) {
        return;
      }

      const targetLeft = Math.max(columnIndex * (this.dayCellWidth + 8) - 160, 0);
      this.$refs.heatmap.scrollTo({
        left: targetLeft,
        behavior: "smooth",
      });
    },
  },
  watch: {
    focusedDay() {
      this.$nextTick(() => {
        this.scrollToFocusedDay();
      });
    },
  },
};
</script>
